import React, { useState } from "react";
import { API } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { AlertOctagon, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

const TIPOS = [
  { v: "reclamacao", label: "Reclamação", desc: "Algo não está funcionando bem" },
  { v: "denuncia", label: "Denúncia", desc: "Situação grave que precisa ser investigada" },
  { v: "sugestao", label: "Sugestão", desc: "Ideia para melhorar" },
  { v: "elogio", label: "Elogio", desc: "Reconhecer um trabalho bem feito" },
];

const MASCOT = "https://customer-assets-lqy194kg.emergentagent.net/job_cooperative-web-1/artifacts/i03h9wld_BONECO%20PRO%20CANAL.PNG";

export default function OuvidoriaPage() {
  const [form, setForm] = useState({
    tipo: "", community: "", contact: "", description: "",
    wants_return: false, anonymous: false, name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tipo) return toast.error("Selecione o tipo de manifestação.");
    if ((form.description || "").trim().length < 10) return toast.error("Descreva sua manifestação (mínimo 10 caracteres).");
    if (form.wants_return && !form.contact.trim()) return toast.error("Para receber retorno, informe telefone ou e-mail.");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/ombudsman/manifestar`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Erro ao enviar.");
      setDone(j);
      toast.success("Manifestação registrada!");
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="container mx-auto px-6 py-16 max-w-2xl text-center" data-testid="ouvidoria-success">
        <div className="w-16 h-16 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h1 className="font-display text-3xl text-forest-900 mb-3">Manifestação registrada</h1>
        <p className="text-forest-900/70 mb-5">
          Sua {done.tipo_label} foi encaminhada ao Setor Social/Secretaria da COOMFLONA.
        </p>
        <div className="bg-cream-100 border border-forest-700/40 p-5 inline-block">
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">Protocolo</div>
          <div className="font-mono text-2xl text-forest-900 mt-1" data-testid="ouvidoria-protocol">{done.protocol}</div>
        </div>
        {done.anonymous && <p className="text-xs text-forest-900/60 mt-4">Sua identidade permanece anônima. Guarde este protocolo para acompanhar.</p>}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-10 max-w-4xl" data-testid="ouvidoria-page">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700 flex items-center gap-1"><AlertOctagon className="w-3.5 h-3.5" /> Canal de Reclamações COOMFLONA</div>
          <h1 className="font-display text-4xl md:text-5xl text-forest-900 mt-2 leading-tight">Sua opinião é importante para nós</h1>
          <p className="text-forest-900/80 mt-3 text-sm">
            Este espaço foi criado para receber <b>reclamações, denúncias, sugestões e elogios</b> relacionados à COOMFLONA.
            Você pode se identificar ou realizar sua manifestação de forma <b>anônima</b>.
          </p>
        </div>
        <img src={MASCOT} alt="Mascotes COOMFLONA" data-testid="ouvidoria-mascot" className="w-56 md:w-72 lg:w-80 mx-auto md:mx-0 object-contain drop-shadow-[0_10px_25px_rgba(0,0,0,0.15)]" />
      </div>

      <form onSubmit={submit} className="bg-white border border-cream-400 p-6 space-y-5" data-testid="ouvidoria-form">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium mb-2 block">Tipo de manifestação *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TIPOS.map((t) => (
              <button type="button" key={t.v} onClick={() => setForm({ ...form, tipo: t.v })}
                data-testid={`ouv-tipo-${t.v}`}
                className={`p-3 text-left text-sm border ${form.tipo === t.v ? "bg-forest-700 text-cream-100 border-forest-700" : "bg-cream-100 border-cream-400 text-forest-900 hover:border-forest-700"}`}>
                <div className="font-medium">{t.label}</div>
                <div className={`text-xs mt-1 ${form.tipo === t.v ? "text-cream-100/80" : "text-forest-900/60"}`}>{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Comunidade</span>
          <Input value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })}
            data-testid="ouv-community" placeholder="Ex.: Maguari" className="mt-2" />
        </label>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Telefone ou e-mail (opcional)</span>
            <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
              data-testid="ouv-contact" placeholder="Somente para retorno" className="mt-2" />
          </label>
          <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Seu nome (opcional)</span>
            <Input value={form.name} disabled={form.anonymous}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="ouv-name" placeholder="Deixe em branco para anônimo" className="mt-2" />
          </label>
        </div>

        <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-forest-900/70 font-medium">Descreva sua manifestação *</span>
          <Textarea rows={6} required minLength={10} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="ouv-description" placeholder="Descreva com detalhes o que aconteceu / sua sugestão / o elogio..."
            className="mt-2" />
        </label>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.wants_return} data-testid="ouv-wants-return"
              onChange={(e) => setForm({ ...form, wants_return: e.target.checked })} />
            Desejo receber retorno
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.anonymous} data-testid="ouv-anonymous"
              onChange={(e) => setForm({ ...form, anonymous: e.target.checked, name: e.target.checked ? "" : form.name })} />
            Enviar de forma anônima
          </label>
        </div>

        <div className="bg-copaiba-100 border border-copaiba-300 p-3 text-xs text-forest-900 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Privacidade:</b> As informações fornecidas serão tratadas com confidencialidade e utilizadas
            exclusivamente para análise e encaminhamento da manifestação.
          </div>
        </div>

        <Button type="submit" disabled={submitting} data-testid="ouv-submit"
          className="w-full bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none py-6 text-base">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : "Enviar manifestação"}
        </Button>
      </form>

      <p className="text-center text-xs text-forest-900/60 mt-6">
        COOMFLONA – Cooperativa Mista da Floresta Nacional do Tapajós<br />
        Sua participação contribui para uma cooperativa cada vez melhor.
      </p>
    </div>
  );
}
